// Phase 4 Gap 2 (P4) — PowerupTargetSelector.
//
// Reusable full-screen DOM target-selection overlay for the cell/piece-
// targeted power-ups that previously had NO target-pick surface (the
// production tray committed an empty payload, which the server REJECTS with
// StateConflictError('phase_b','powerup_invalid_payload') / 'no_target').
//
// Cloned structurally from MulliganSelector (the established DOM-driveable
// overlay template — full-screen, maps playerView.board to one
// <button data-…-cell-idx={cell_idx}> per cell, eligibility-gated disabled
// cells, a bottom Confirm/Cancel action bar). It is pure DOM (NOT the Pixi
// QuillTargetOverlay) so Playwright can click the cell buttons directly.
//
// Two modes:
//   • mode='single'  — castle_keep / tongue_tie / knights_vow. A single click
//     on an eligible cell selects it; Confirm calls onConfirmSingle(cellIdx).
//   • mode='vanishing' — the_vanishing. Two-step: step 1 picks the CALLER-
//     owned source piece (eligibleFrom); step 2 picks an EMPTY destination on
//     the caller's back two ranks (eligibleTo). Confirm calls
//     onConfirmVanishing(fromCellIdx, toCellIdx). A visible step indicator +
//     distinct data-attrs (data-vanishing-from-cell-idx /
//     data-vanishing-to-cell-idx) keep each step DOM-driveable.
//
// Eligibility predicates are supplied by the caller (MatchScreen), derived
// from + cross-checked against the backend effect files
// (apps/backend/src/match/powerups/*.ts) so a valid client selection is
// accepted by the authoritative server.
//
// Locks (inherited from MulliganSelector):
//   - NO browser-storage caching (in-memory selection only).
//   - Reads ONLY playerView.board (public-by-construction) — no opponent
//     private-field reads.
//   - Pure DOM — no Pixi/Group instantiation.

import { type JSX, useCallback, useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import type { PlayerView } from '../../game/PlayerViewSchema'
import { Btn } from '../components/Btn'
import { useAnnounce } from '../components/useAnnounce'
import { useFocusTrap } from '../components/useFocusTrap'

type BoardCell = PlayerView['board'][number]
type Eligible = (cell: BoardCell) => boolean

interface CommonProps {
  readonly board: ReadonlyArray<BoardCell>
  /** Visible + SR title for the overlay (an i18n message id). */
  readonly titleMessageId: string
  readonly onCancel: () => void
}

interface SingleProps extends CommonProps {
  readonly mode: 'single'
  readonly eligible: Eligible
  readonly onConfirmSingle: (cellIdx: number) => void
}

interface VanishingProps extends CommonProps {
  readonly mode: 'vanishing'
  /** Step 1 — source piece eligibility (caller-owned piece). */
  readonly eligibleFrom: Eligible
  /** Step 2 — destination eligibility (empty caller back-rank cell). */
  readonly eligibleTo: Eligible
  readonly onConfirmVanishing: (fromCellIdx: number, toCellIdx: number) => void
}

export type PowerupTargetSelectorProps = SingleProps | VanishingProps

/** Columns per board row. Runespring fields are 6 wide; rows derive from
 *  board.length / 6 so a grown board (48 / 60 cells) renders correctly. */
const COLS = 6

export function PowerupTargetSelector(
  props: PowerupTargetSelectorProps,
): JSX.Element {
  const { board, titleMessageId, onCancel } = props
  const intl = useIntl()
  const announce = useAnnounce()
  const dialogRef = useRef<HTMLDivElement>(null)

  useFocusTrap(dialogRef, true, onCancel)

  // Two-step state for vanishing: which step we're on + the chosen source.
  const [vanishingFrom, setVanishingFrom] = useState<number | null>(null)
  // Single-mode (and the per-step) current selection.
  const [selected, setSelected] = useState<number | null>(null)

  useEffect(() => {
    announce(intl.formatMessage({ id: titleMessageId }), 'polite')
  }, [announce, intl, titleMessageId])

  // Resolve the eligibility predicate + data-attr for the CURRENT step.
  const isVanishing = props.mode === 'vanishing'
  const onSourceStep = isVanishing && vanishingFrom === null
  const eligibleForStep: Eligible =
    props.mode === 'single'
      ? props.eligible
      : onSourceStep
        ? props.eligibleFrom
        : props.eligibleTo

  const dataAttr = !isVanishing
    ? 'data-powerup-target-cell-idx'
    : onSourceStep
      ? 'data-vanishing-from-cell-idx'
      : 'data-vanishing-to-cell-idx'

  const handleCellTap = useCallback(
    (cell: BoardCell) => {
      if (!eligibleForStep(cell)) return
      if (props.mode === 'vanishing' && onSourceStep) {
        // Step 1 commit — lock the source, advance to destination step.
        setVanishingFrom(cell.cell_idx)
        setSelected(null)
        announce(
          intl.formatMessage({ id: 'match.powerup_target.vanishing.step_to' }),
          'polite',
        )
        return
      }
      // Single mode OR vanishing step 2 — toggle selection.
      setSelected((prev) => (prev === cell.cell_idx ? null : cell.cell_idx))
    },
    [eligibleForStep, props.mode, onSourceStep, announce, intl],
  )

  const handleConfirm = useCallback(() => {
    if (selected === null) return
    if (props.mode === 'single') {
      props.onConfirmSingle(selected)
      return
    }
    if (vanishingFrom === null) return
    props.onConfirmVanishing(vanishingFrom, selected)
  }, [selected, vanishingFrom, props])

  // the_vanishing step 2 → step 1 rewind. Clears the locked source + any
  // destination selection and re-enables the source-pick step WITHOUT closing
  // the overlay (cancel is the only path that fully closes). Bounded: only
  // reachable in step 2 (vanishingFrom !== null); resets to step 1.
  const handleBack = useCallback(() => {
    setVanishingFrom(null)
    setSelected(null)
    announce(
      intl.formatMessage({
        id: 'match.powerup_target.vanishing.step_from',
      }),
      'polite',
    )
  }, [announce, intl])

  // Step-aware description (referenced by aria-describedby). For single mode it
  // is the overlay title; for vanishing it reflects the current step.
  const descriptionId = 'powerup-target-desc'
  const descriptionMessageId = !isVanishing
    ? titleMessageId
    : onSourceStep
      ? 'match.powerup_target.vanishing.step_from'
      : 'match.powerup_target.vanishing.step_to'

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="powerup-target-title"
      aria-describedby={descriptionId}
      data-powerup-target-overlay
      data-powerup-target-mode={props.mode}
      data-powerup-target-step={
        isVanishing ? (onSourceStep ? 'from' : 'to') : 'single'
      }
      ref={dialogRef}
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'transparent',
        pointerEvents: 'auto',
        zIndex: 50,
      }}
    >
      {/* Visually-hidden title + step-aware description (referenced via
          aria-labelledby / aria-describedby). The visible title is the
          instruction pill below. */}
      <h2 id="powerup-target-title" className="sr-only">
        <FormattedMessage id={titleMessageId} />
      </h2>
      <p id={descriptionId} className="sr-only">
        <FormattedMessage id={descriptionMessageId} />
      </p>

      {/* Top instruction pill — shows the overlay title, plus the step
          indicator for the two-step vanishing flow. */}
      <div
        data-powerup-target-pill
        style={{
          position: 'fixed',
          // ARCA-155 — align to the real board top (stamped by MatchScreen as
          // --board-top); the 80px fallback is the pre-measurement value.
          top: 'var(--board-top, 80px)',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: 'var(--space-2) var(--space-4)',
          background: 'var(--color-surface-mid)',
          color: 'var(--color-text-primary)',
          border: '2px solid var(--color-surface-stroke)',
          borderRadius: 9999,
          fontFamily: 'var(--font-family-display)',
          fontWeight: 700,
          fontSize: 'var(--font-size-16)',
          boxShadow: 'var(--shadow-pop)',
          zIndex: 60,
          textAlign: 'center',
        }}
      >
        <FormattedMessage id={titleMessageId} />
        {isVanishing ? (
          <>
            {' — '}
            <FormattedMessage
              id={
                onSourceStep
                  ? 'match.powerup_target.vanishing.step_from'
                  : 'match.powerup_target.vanishing.step_to'
              }
            />
          </>
        ) : null}
      </div>

      <div
        data-powerup-target-grid
        style={{
          // ARCA-155 — align the letter grid's top edge to board grid row 0 via
          // the measured --board-top (stamped on <main> by MatchScreen). The old
          // hardcoded marginTop:144 sat ~one cell-row below the real board top
          // (~72px + 8px board-stack pad), floating the bottom row over the emote
          // bar. The 80px fallback (≈ the board top, shared with the pill) is a
          // safety net for the brief window before the layout-effect measurement
          // lands — never the buggy 144.
          marginTop: 'var(--board-top, 80px)',
          padding: 'var(--space-4)',
          display: 'grid',
          gridTemplateColumns: `repeat(${COLS}, 1fr)`,
          gap: 'var(--space-2)',
          maxWidth: 480,
          width: '100%',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        {board.map((cell) => {
          const eligible = eligibleForStep(cell)
          const isSelected = selected === cell.cell_idx
          const isVanishingSource =
            isVanishing && vanishingFrom === cell.cell_idx
          // Render the appropriate data-attr per the current step so the e2e
          // driver / Playwright can target the right hit surface.
          const cellAttrs: Record<string, number> = {
            [dataAttr]: cell.cell_idx,
          }
          return (
            <button
              key={`powerup-target-cell-${cell.cell_idx}`}
              type="button"
              {...cellAttrs}
              data-powerup-target-cell-eligible={eligible ? 'true' : 'false'}
              data-powerup-target-cell-selected={isSelected ? 'true' : 'false'}
              disabled={!eligible}
              onClick={() => handleCellTap(cell)}
              aria-label={`${cell.letter ?? ''}${isSelected ? ' (selected)' : ''}`}
              aria-pressed={isSelected}
              style={{
                position: 'relative',
                aspectRatio: '1 / 1',
                padding: 0,
                background: 'var(--color-surface-mid)',
                color: 'var(--color-text-primary)',
                border:
                  isSelected || isVanishingSource
                    ? '3px solid var(--color-cta-primary)'
                    : '2px solid var(--color-surface-stroke)',
                borderRadius: 'var(--radius-md)',
                cursor: eligible ? 'pointer' : 'not-allowed',
                opacity: eligible ? 1 : 0.45,
                fontFamily: 'var(--font-family-display)',
                fontWeight: 700,
                fontSize: 'var(--font-size-22)',
              }}
            >
              {cell.letter ?? ''}
            </button>
          )
        })}
      </div>

      <div
        data-powerup-target-action-bar
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          padding: 'var(--space-4) var(--space-4) var(--space-5)',
          background: 'var(--color-surface-mid)',
          display: 'flex',
          gap: 'var(--space-2)',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 60,
        }}
      >
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Btn kind="ghost" size="md" onClick={onCancel}>
            <FormattedMessage id="match.powerup_target.cancel" />
          </Btn>
          {/* the_vanishing step 2 only — rewind to the source-pick step
              (additive; cancel still fully closes). */}
          {isVanishing && vanishingFrom !== null ? (
            <Btn
              kind="ghost"
              size="md"
              testId="powerup-target-back"
              onClick={handleBack}
            >
              <FormattedMessage id="match.powerup_target.back" />
            </Btn>
          ) : null}
        </div>
        <Btn
          kind="primary"
          size="lg"
          testId="powerup-target-confirm"
          disabled={selected === null}
          onClick={handleConfirm}
        >
          <FormattedMessage id="match.powerup_target.confirm" />
        </Btn>
      </div>
    </div>
  )
}
